import { Box, Button, Dialog, IconButton, MenuItem, Select, Typography, Stack, Tooltip, Divider, Fab, DialogContent, InputAdornment, TextField } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { GridRowId, GridColDef } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useParams } from "react-router";
import axios from "axios";

// Icons
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'; 

// Logic & Tools
import { IDGFIP } from "../../../interface/dgfip";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import { showSnackbar } from "../../../redux/actions";
import { urlAPI } from "../../../config/config";
import getDgfipData from "../../../tools/forecast/getDgfipData";
import AddUpdateDGFIP from "../AddUpdate/AddUpdateDGFIP";
import { addUpdateDGFIP } from "../../../tools/forecast/addUpdateDGFIP";
import { duplicateDGFIP } from "../../../tools/forecast/duplicateDGFIP";
import { setTotalByMonth } from "../../../redux/actions/forecastActions";

// Components
import AlerteDialog from "../../AlerteDialog/AlerteDialog";
import CustomDataGrid from "../../customDataGrid/CustomDataGrid";

const DGFIP = ({ forecastVersion, forecastDates }: { forecastVersion: number | undefined, forecastDates: string }) => {
    const dispatch = useDispatch();
    const params = useParams();
    const token = useSelector((state: RootState) => state.user.token);

    const [dgfipData, setDgfipData] = useState<IDGFIP[]>([]);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [editId, setEditId] = useState<string>("");
    const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
    const [searchLabel, setSearchLabel] = useState<string>("");
    
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [actionType, setActionType] = useState<"delete" | "close" | null>(null);

    const monthList = useMemo(() => getForecastMonthList(forecastDates), [forecastDates]);

    // Chargement des données
    useEffect(() => {
        getDgfipData(params.id, forecastVersion, setDgfipData, token, dispatch, () => {});
    }, [forecastVersion, openModal, params.id]);

    // --- COLONNES ---
    const columns: GridColDef[] = [
        { 
            field: "label", 
            headerName: "Libellé / Taxe", 
            minWidth: 300, 
            flex: 1,
            renderCell: (params: any) => (
                <Box sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                        {params.row.label}
                    </Typography>
                </Box>
            )
        },
        ...monthList.map((m) => ({
            field: m.field,
            headerName: m.name,
            minWidth: 120,
            align: "right" as const,
            headerAlign: "right" as const,
        }))
    ];

    // --- LIGNES ET TOTAUX ---
    const rows = useMemo(() => {
        // 1. Filtrage
        const filtered = dgfipData.filter(d => 
            d.label.toLowerCase().includes(searchLabel.toLowerCase())
        );

        // 2. Transformation pour le DataGrid
        const dataRows = filtered.map((item: any) => { // On utilise : any ici pour lever l'ambiguïté
            const row: any = { id: item._id, label: item.label };
            
            // On s'assure que months est bien un tableau avant le forEach
            const monthsArray = Array.isArray(item.months) ? item.months : [];
            
            monthList.forEach(m => {
                const monthEntry = monthsArray.find((mo: any) => mo.label === m.field);
                const val = monthEntry ? monthEntry.value : 0;
                
                row[m.field] = `${Number(val).toLocaleString()} €`;
                row[`raw_${m.field}`] = Number(val);
            });
            return row;
        });

        // 3. Calcul des totaux (reste identique)
        const totalsRedux: any = {};
        const totalRowValues: any = {};

        monthList.forEach(m => {
            const sum = dataRows.reduce((acc, row) => acc + (row[`raw_${m.field}`] || 0), 0);
            totalsRedux[m.field] = sum;
            totalRowValues[m.field] = `${sum.toLocaleString()} €`;
        });

        dispatch(setTotalByMonth("DGFIP", totalsRedux));

        if (dataRows.length === 0) return [];

        return [
            ...dataRows, 
            { id: "total", label: "TOTAL DGFIP", ...totalRowValues }
        ];
    }, [dgfipData, searchLabel, monthList, dispatch]);

    // --- ACTIONS ---
    const confirmDelete = async () => {
        try {
            await Promise.all(selectedRows.map(id => 
                axios.delete(`${urlAPI}/forecast/dgfip/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ));
            setDgfipData(prev => prev.filter(d => !selectedRows.includes(d._id)));
            setSelectedRows([]);
            setShowConfirmDialog(false);
            dispatch(showSnackbar("Frais DGFIP supprimés", "success"));
        } catch (error) { dispatch(showSnackbar("Erreur lors de la suppression", "error")); }
    };

    const handleDuplication = () => {
        duplicateDGFIP(selectedRows[0] as string, token, setOpenModal, dispatch);
        setSelectedRows([]);
    };

    return (
        <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            
            {/* --- HEADER STYLE INVEST --- */}
            <Box sx={{ bgcolor: "white", p: 3, borderRadius: 4, mb: 4, border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ bgcolor: "#fff7ed", p: 1, borderRadius: 2, display: 'flex' }}>
                            <AccountBalanceIcon sx={{ color: "#f59e0b" }} />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>DGFIP</Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748b", bgcolor: "#f1f5f9", px: 1.5, py: 0.5, borderRadius: 1 }}>
                        VERSION {forecastVersion}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <TextField 
                        size="small" 
                        placeholder="Rechercher une taxe..." 
                        value={searchLabel} 
                        onChange={(e) => setSearchLabel(e.target.value)}
                        sx={{ bgcolor: "#f1f5f9", borderRadius: 2, "& fieldset": { border: "none" }, minWidth: 350 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled"/></InputAdornment> }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => { setEditId(""); setOpenModal(true); }} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 3, bgcolor: "#f59e0b", "&:hover": { bgcolor: "#d97706" } }}>
                        Ajouter un frais
                    </Button>
                </Box>
            </Box>

            {/* --- TABLEAU --- */}
            <Box sx={{ mb: 12 }}>
                <CustomDataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    checkboxSelection
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
                    isRowSelectable={(params: any) => params.row.id !== 'total'}
                    onCellClick={(params: any) => {
                        if (params.row.id !== 'total' && params.field !== "__check__") {
                            setEditId(params.row.id);
                            setOpenModal(true);
                        }
                    }}
                    sx={{
                        '& .MuiDataGrid-row[data-id="total"]': {
                            bgcolor: '#fffbeb !important',
                            fontWeight: '800 !important',
                            '& .MuiDataGrid-cell': { color: '#b45309', borderTop: '2px solid #fde68a' },
                            '& .MuiDataGrid-checkboxInput': { display: 'none' }
                        }
                    }}
                />
            </Box>

            {/* --- DOCK FLOTTANT --- */}
            {selectedRows.length > 0 && (
                <Box sx={{ position: "fixed", bottom: 30, right: "50%", transform: "translateX(50%)", display: "flex", alignItems: "center", gap: 2, bgcolor: "#1e293b", p: "12px 24px", borderRadius: "100px", boxShadow: "0px 10px 40px rgba(0,0,0,0.2)", zIndex: 1100, color: "white" }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedRows.length} sélectionnés</Typography>
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
                    <Tooltip title="Supprimer"><Fab color="error" size="small" onClick={() => { setActionType("delete"); setShowConfirmDialog(true); }}><DeleteIcon fontSize="small" /></Fab></Tooltip>
                    {selectedRows.length === 1 && (
                        <Tooltip title="Dupliquer"><Fab color="warning" size="small" onClick={handleDuplication}><ContentCopyIcon fontSize="small" /></Fab></Tooltip>
                    )}
                    <IconButton size="small" onClick={() => setSelectedRows([])} sx={{ color: "white" }}><CloseIcon /></IconButton>
                </Box>
            )}

            {/* MODALE DGFIP */}
            <Dialog open={openModal} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 4 } }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{editId ? "Modifier" : "Ajouter"} un frais DGFIP</Typography>
                    <IconButton onClick={() => { setActionType("close"); setShowConfirmDialog(true); }}><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    <AddUpdateDGFIP 
                        dgfipId={editId} 
                        handleSubmit={addUpdateDGFIP} 
                        forecastId={params.id} 
                        version={forecastVersion} 
                        setOpenModal={setOpenModal} 
                        monthList={monthList} 
                        setShowConfirmDialog={setShowConfirmDialog} 
                    />
                </DialogContent>
            </Dialog>

            <AlerteDialog 
                open={showConfirmDialog} 
                handleClose={() => setShowConfirmDialog(false)} 
                deleteProject={actionType === "delete" ? confirmDelete : () => { setShowConfirmDialog(false); setOpenModal(false); }} 
                title={actionType === "delete" ? "Supprimer ?" : "Abandonner ?"} 
                contentText={actionType === "delete" ? `Voulez-vous supprimer ces ${selectedRows.length} éléments ?` : "Voulez-vous quitter sans enregistrer ?"} 
                color="error" 
            />
        </Box>
    );
};

export default DGFIP;